import Util from '../../../forecast-app/shared/util/util';
import UpdateApprovalStatusMutation from '../../../mutations/UpdateApprovalStatusMutation';
import {APPROVAL_STATUS, APPROVAL_TYPE, ESTIMATION_UNIT} from '../../../constants';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import {REJECTION_MODAL_VARIANT} from '../../../containers/modal/time_approval/RejectSubmissionModal';
import {graphql} from 'react-relay';
import {createToast} from '../../../forecast-app/shared/components/toasts/toast';
import {personIsVirtualUser} from '../../../forecast-app/shared/util/PermissionsUtil';
import {showApprovalErrorModal} from './ApprovalErrors';
import {hasFeatureFlag} from '../../../forecast-app/shared/util/FeatureUtil';
import {profilePicSrc} from '../../../directApi';

const TIMESHEET_APPROVAL_NEW_STATUSES_FLAG = 'timesheet_approval_new_statuses';
const USE_SAGE_SERVICE_FOR_TIME_REGS_FLAG = 'use_sage_service_for_time_regs';

export const hasNewApprovalStatuses = () =>
	hasFeatureFlag(TIMESHEET_APPROVAL_NEW_STATUSES_FLAG) && !hasFeatureFlag(USE_SAGE_SERVICE_FOR_TIME_REGS_FLAG);

/**
 * The statuses the company-wide timesheet lock will force-approve. Used to fetch the entries the
 * lock modal lists as "about to be approved", so it has to match the eligible set the backend's
 * force-approve sweep applies: any status listed here that the backend then skips is a promise the
 * lock does not keep, and any status it approves but this omits is time the admin was not warned
 * about.
 *
 * DECLINED is absent under the new statuses: a decline is an approver's explicit decision and the
 * lock leaves those rows declined.
 *
 * Without the flag all four are named, because the flag-off sweep takes all four. A legacy client
 * cannot render PENDING or DECLINED — both project to it as NOT_SUBMITTED — but this list is a
 * search filter matched against raw stored values, not something the user reads, so naming them is
 * what makes those rows appear in the preview at all. Listing only the pair the client can name
 * matched neither, so the admin was warned about none of that time. The flag-off sweep approving
 * declined time is deliberate — a legacy account has no verb that could clear it otherwise — which
 * leaves this preview as the only thing standing between the admin and a silent approval.
 *
 * Note TimeRegLogic's APPROVAL_STATUS filter auto-adds NOT_SUBMITTED to any non-exclude filter
 * naming PENDING or DECLINED; NOT_SUBMITTED is named explicitly here, so that widening is a no-op.
 */
export const forceApprovableStatuses = () =>
	hasNewApprovalStatuses()
		? [APPROVAL_STATUS.NOT_SUBMITTED, APPROVAL_STATUS.SUBMITTED, APPROVAL_STATUS.PENDING]
		: [APPROVAL_STATUS.NOT_SUBMITTED, APPROVAL_STATUS.SUBMITTED, APPROVAL_STATUS.PENDING, APPROVAL_STATUS.DECLINED];

// Approval types that require the TIMESHEET_APPROVAL_NEW_STATUSES_FLAG to be enabled.
// Sending any of these to a company that does not have the flag will be rejected by the backend.
const NEW_STATUSES_APPROVAL_TYPES = new Set([
	APPROVAL_TYPE.DECLINE,
	APPROVAL_TYPE.RESUBMIT,
	APPROVAL_TYPE.INVOICE,
	APPROVAL_TYPE.REVERSE_APPROVAL,
]);

export const isApprovalTypeAvailable = approvalType => {
	if (!approvalType) return false;
	if (NEW_STATUSES_APPROVAL_TYPES.has(approvalType)) {
		return hasNewApprovalStatuses();
	}
	return true;
};

export const GROUP_BY = {
	PERSON: 'person',
	PROJECT: 'project',
};

const NO_APPROVAL_STATUS_CHANGE_REASON = {
	INVOICED: 'INVOICED',
	LOCKED_PERIOD: 'LOCKED_PERIOD',
	COMPANY_LOCK: 'COMPANY_LOCK',
	HAS_LINKED_ALLOCATION: 'HAS_LINKED_ALLOCATION',
};

/**
 * The confirmation toast for an applied approval action, or undefined when the action has none.
 *
 * Exported for testing: the missing cases below were invisible precisely because nothing asserted
 * on them.
 */
export const getToastMessage = (person, approvalType, intl) => {
	if (!person) return intl.formatMessage({id: 'approval.project_approved'});
	const name = person.firstName || person.lastName;
	switch (approvalType) {
		case APPROVAL_TYPE.UNAPPROVE:
		case APPROVAL_TYPE.UNSUBMIT:
			return intl.formatMessage({id: 'approval.person_timesheet_rejected'}, {person: name});
		case APPROVAL_TYPE.APPROVE:
			return intl.formatMessage({id: 'approval.person_timesheet_approved'}, {person: name});
		// DECLINE and REVERSE_APPROVAL used to fall through to undefined. They are the two
		// actions that open a reason modal first, so they were also the only two that confirmed
		// nothing on success — the modal closed and the row looked unchanged, which is
		// indistinguishable from the action having been rejected.
		case APPROVAL_TYPE.DECLINE:
			return intl.formatMessage({id: 'approval.person_timesheet_declined'}, {person: name});
		case APPROVAL_TYPE.REVERSE_APPROVAL:
			return intl.formatMessage({id: 'approval.person_timesheet_approval_reversed'}, {person: name});
		default:
			return undefined;
	}
};

export const UpdateApprovalStatus = (
	personIds,
	projectIds,
	idleTimeIds,
	approvalType,
	currentViewingDate,
	intl,
	person,
	isTimeOffOnly,
	onComplete
) => {
	// Backwards-compat guard: the new approval types (DECLINE/RESUBMIT/INVOICE/REVERSE_APPROVAL)
	// are only valid when the timesheet_approval_new_statuses feature flag is enabled
	// Reject them on the client so we don't round-trip to a server error.
	if (!isApprovalTypeAvailable(approvalType)) {
		console.warn(
			`UpdateApprovalStatus: approvalType "${approvalType}" requires the timesheet_approval_new_statuses feature flag. Skipping mutation.`
		);
		if (onComplete) onComplete();
		return;
	}
	const startDate = currentViewingDate.clone().startOf('week');
	const endDate = currentViewingDate.clone().endOf('week');
	const update = message => {
		const onSuccess = res => {
			// The mutation reports backend refusals in `errors` rather than by failing, so
			// this handler also runs for actions the back end rejected. Show the refusal and skip the
			// toast — previously a rejected APPROVE still toasted "approved", and a rejected reverse
			// approval said nothing at all.
			if (!showApprovalErrorModal(res.updateApprovalStatus?.errors)) {
				const toastMessage = getToastMessage(person, approvalType, intl);
				if (toastMessage) {
					createToast({duration: 4000, message: toastMessage});
				}
			}
			onComplete();
		};
		const mutationData = {
			personIds: personIds,
			projectIds: projectIds,
			idleTimeIds: idleTimeIds,
			approvalType: approvalType,
			startYear: startDate.year(),
			startMonth: startDate.month() + 1,
			startDay: startDate.date(),
			endYear: endDate.year(),
			endMonth: endDate.month() + 1,
			endDay: endDate.date(),
			sendNotification: true,
			includeTimeOff: true,
			// PIKA-519: passes the same week range as the parent
			// ownedProjectPersonsQuery so the mutation's persons.weekApprovals
			// response merges into the same Relay cache slot (otherwise the row
			// stays on the Submitted tab even though the backend wrote APPROVED).
			weekStart: startDate.format('YYYY-MM-DD'),
			weekEnd: endDate.format('YYYY-MM-DD'),
		};
		if (message) {
			mutationData.rejectionMessage = message;
			const useDeclineReason = approvalType === APPROVAL_TYPE.DECLINE || approvalType === APPROVAL_TYPE.REVERSE_APPROVAL;
			if (useDeclineReason) {
				mutationData.declineReason = message;
			}
		}
		Util.CommitMutation(UpdateApprovalStatusMutation, mutationData, onSuccess);
	};

	const getModalVariant = () => {
		if (approvalType === APPROVAL_TYPE.DECLINE) return REJECTION_MODAL_VARIANT.DECLINE;
		if (approvalType === APPROVAL_TYPE.REVERSE_APPROVAL) return REJECTION_MODAL_VARIANT.REVERSE;
		return REJECTION_MODAL_VARIANT.REJECT;
	};

	const showsModal =
		!!personIds &&
		personIds.length === 1 &&
		(approvalType === APPROVAL_TYPE.UNSUBMIT ||
			approvalType === APPROVAL_TYPE.DECLINE ||
			approvalType === APPROVAL_TYPE.REVERSE_APPROVAL);

	// if only one person, we show modal for setting a message for the email, otherwise we just send the email
	if (showsModal) {
		showModal({
			type: MODAL_TYPE.REJECT_TIMEAPPROVAL_SUBMISSION,
			startDate,
			endDate,
			rejectForName: person.fullName,
			isTimeOffOnly,
			variant: getModalVariant(),
			onReject: update,
			onCancel: onComplete,
		});
	} else {
		update();
	}
};

const getNoApprovalStatusChangeForGroup = timeRegs => {
	if (timeRegs && timeRegs.length > 0) {
		// Any time registration in the group with one of these statuses instantly disqualifies group from Approval / Rejection
		const singleNoApprovalChangeTypes = [
			NO_APPROVAL_STATUS_CHANGE_REASON.COMPANY_LOCK,
			NO_APPROVAL_STATUS_CHANGE_REASON.HAS_LINKED_ALLOCATION,
			NO_APPROVAL_STATUS_CHANGE_REASON.LOCKED_PERIOD,
		];
		const lockedOrLinkedTimeReg = timeRegs.find(timeReg =>
			singleNoApprovalChangeTypes.includes(timeReg.noApprovalStatusChange)
		);
		if (lockedOrLinkedTimeReg) {
			return lockedOrLinkedTimeReg.noApprovalStatusChange;
		}

		// Approval / Rejection should only be disabled if all time regs in group are invoiced
		if (timeRegs.every(timeReg => timeReg.noApprovalStatusChange === NO_APPROVAL_STATUS_CHANGE_REASON.INVOICED)) {
			return NO_APPROVAL_STATUS_CHANGE_REASON.INVOICED;
		}
	}

	return null;
};

/**
 * Build one tab's rows: a row per person with time regs in `datas`, plus a placeholder row for
 * each person in `otherPerson` who logged nothing.
 *
 * `personIdsWithRows` is the set of people who already appear on some rendered tab through real
 * rows; anyone in it gets no placeholder here. It has to describe the whole week, not the tabs
 * assembled so far — a partially built answer says "no" for every bucket not yet assembled,
 * which is what put an hours-less placeholder on one tab for a person whose rows live on
 * another. Build it once for the week and pass the same set to every bucket.
 *
 * Two things the caller owns:
 *   - Include only the statuses this flag state actually renders. A person whose whole week
 *     sits in a bucket that is never drawn (a raw INVOICED row with the new statuses off, say)
 *     would otherwise be suppressed here without appearing anywhere, and drop off the page
 *     instead of showing as a 0h row.
 *   - Placeholder-against-placeholder suppression is not covered, because the set is built from
 *     real rows only. That is safe only while each person is synthesized into at most one
 *     bucket, as `synthesizeOwnedPersonsByStatus` guarantees; relax that partition and the same
 *     person gets a placeholder row on two tabs.
 */
export const getPersonData = (datas, parentInfo, otherPerson, personIdsWithRows, personFilter, filterOptions) => {
	let personData = [];
	const parentId = parentInfo?.id;
	const parentGroupType = parentInfo?.groupType;

	datas &&
		datas.forEach(data => {
			const {id, firstName, lastName, fullName, profilePictureId, email} = data.person;
			const timeRegDate = Util.CreateNonUtcMomentDate(data.year, data.month, data.day);
			const dayIdx = timeRegDate.weekday();
			const idx = personData.findIndex(r => r.person.id === id);
			if (idx > -1) {
				personData[idx].person.timeRegs.push(data);
				personData[idx].person.totals[dayIdx] += data.minutesRegistered;
				personData[idx].person.billableTotals[dayIdx] += data.billableMinutesRegistered || 0;
				personData[idx].person.total += data.minutesRegistered;
				personData[idx].person.billableTotal += data.billableMinutesRegistered;
			} else {
				const minutesRegisteredTotals = [null, null, null, null, null, null, null];
				minutesRegisteredTotals[dayIdx] = data.minutesRegistered;
				const billableTimeRegsTotals = [null, null, null, null, null, null, null];
				billableTimeRegsTotals[dayIdx] = data.billableMinutesRegistered || 0;
				personData.push({
					person: {
						id: id,
						firstName: firstName,
						lastName: lastName,
						fullName: fullName,
						email: email,
						profilePictureId: profilePicSrc(profilePictureId),
						timeRegs: [data],
						totals: minutesRegisteredTotals,
						billableTotals: billableTimeRegsTotals,
						total: data.minutesRegistered,
						billableTotal: data.billableMinutesRegistered,
						parentId: parentId,
						parentGroupType: parentGroupType,
						noApprovalStatusChange: data.noApprovalStatusChange,
					},
				});
			}
		});

	personData.sort((a, b) => a.person.fullName.localeCompare(b.person.fullName));

	//add the people who dind't register any time yet
	if (otherPerson) {
		const res2 = [];
		otherPerson &&
			otherPerson
				.filter(
					person =>
						!personIsVirtualUser(person.node) && (personFilter ? personFilter(person.node, filterOptions) : true)
				)
				.forEach(person => {
					const idxNotSubmitted = personData.findIndex(r => r.person.id === person.node.id);
					const appearsInOtherTab = personIdsWithRows ? personIdsWithRows.has(person.node.id) : false;

					if (idxNotSubmitted === -1 && !appearsInOtherTab) {
						res2.push({
							person: {
								id: person.node.id,
								firstName: person.node.firstName,
								lastName: person.node.lastName,
								fullName: person.node.fullName,
								profilePictureId: profilePicSrc(person.node.profilePictureId),
								timeRegs: [],
								totals: [],
								billableTotals: [],
								total: null,
								billableTotal: null,
								parentId: null,
								parentGroupType: null,
								noApprovalStatusChange: null,
							},
						});
					}
				});
		res2.sort((a, b) => a.person.fullName.localeCompare(b.person.fullName));
		personData = [...personData, ...res2];
	}

	personData.forEach(personResult => {
		if (personResult.person) {
			personResult.person.noApprovalStatusChange = getNoApprovalStatusChangeForGroup(personResult.person.timeRegs);
		}
	});

	return personData;
};

export const getProjectData = (datas, parentInfo, includeIdleTimes) => {
	const projectData = [];
	const parentId = parentInfo?.id;
	const parentGroupType = parentInfo?.groupType;

	datas &&
		datas.forEach(data => {
			const timeRegDate = Util.CreateNonUtcMomentDate(data.year, data.month, data.day);
			const dayIdx = timeRegDate.weekday();
			const project = data.task ? data.task.project : data.project;
			const idleTime = data.idleTime;
			if (project) {
				const projectIdx = projectData.findIndex(r => {
					return r.project ? r.project.id === project.id : r.task?.project.id === project.id;
				});
				if (projectIdx > -1) {
					projectData[projectIdx].project.timeRegs.push(data);
					projectData[projectIdx].project.totals[dayIdx] += data.minutesRegistered;
					projectData[projectIdx].project.billableTotals[dayIdx] += data.billableMinutesRegistered || 0;
					projectData[projectIdx].project.timeRegsOnDayIndex[dayIdx].push(data);
					projectData[projectIdx].project.total += data.minutesRegistered;
					projectData[projectIdx].project.billableTotal += data.billableMinutesRegistered;
				} else {
					const timeRegsTotals = [null, null, null, null, null, null, null];
					timeRegsTotals[dayIdx] = data.minutesRegistered;
					const billableTimeRegsTotals = [null, null, null, null, null, null, null];
					billableTimeRegsTotals[dayIdx] = data.billableMinutesRegistered || 0;
					const timeRegsOnDayIndex = [[], [], [], [], [], [], []];
					timeRegsOnDayIndex[dayIdx].push(data);
					const projectOwner = project.projectPersons.edges.map(projectOwner => {
						const person = projectOwner.node.person;
						return {
							fullName: person.fullName,
							imageSource: profilePicSrc(person.profilePictureId),
						};
					});

					projectData.push({
						project: {
							id: project.id,
							companyProjectId: project.companyProjectId,
							customProjectId: project.customProjectId,
							name: project.name,
							projectColor: project.projectColor,
							timeRegs: [data],
							totals: timeRegsTotals,
							billableTotals: billableTimeRegsTotals,
							timeRegsOnDayIndex: timeRegsOnDayIndex,
							total: data.minutesRegistered,
							billableTotal: data.billableMinutesRegistered,
							projectOwner: projectOwner,
							parentId: parentId,
							parentGroupType: parentGroupType,
							noApprovalStatusChange: data.noApprovalStatusChange,
							billable: project.billable,
						},
					});
				}
			}
			if (idleTime && includeIdleTimes) {
				const idleTimeIdx = projectData.findIndex(r => {
					return r.idleTime?.id === idleTime.id;
				});

				if (idleTimeIdx > -1) {
					projectData[idleTimeIdx].idleTime.timeRegs.push(data);
					projectData[idleTimeIdx].idleTime.totals[dayIdx] += data.minutesRegistered;
					projectData[idleTimeIdx].idleTime.billableTotals[dayIdx] = 0;
					projectData[idleTimeIdx].idleTime.total += data.minutesRegistered;
					projectData[idleTimeIdx].idleTime.noApprovalStatusChange =
						projectData[idleTimeIdx].idleTime.noApprovalStatusChange || data.noApprovalStatusChange;
				} else {
					const timeRegsOnDayIndex = [[], [], [], [], [], [], []];
					timeRegsOnDayIndex[dayIdx].push(data);
					const timeRegsTotals = [null, null, null, null, null, null, null];
					const billableTotals = [null, null, null, null, null, null, null];
					timeRegsTotals[dayIdx] = data.minutesRegistered;
					billableTotals[dayIdx] = 0;
					projectData.push({
						idleTime: {
							id: idleTime.id,
							name: idleTime.name,
							isInternalTime: idleTime.isInternalTime,
							timeRegs: [data],
							totals: timeRegsTotals,
							billableTotals: billableTotals,
							timeRegsOnDayIndex: timeRegsOnDayIndex,
							total: data.minutesRegistered,
							billableTotal: 0,
							parentId: parentId,
							parentGroupType: parentGroupType,
							noApprovalStatusChange: data.noApprovalStatusChange,
						},
					});
				}
			}
		});

	projectData.forEach(projectResult => {
		if (projectResult.project) {
			projectResult.project.noApprovalStatusChange = getNoApprovalStatusChangeForGroup(projectResult.project.timeRegs);
		}
	});
	return projectData;
};

export const getTimeData = datas => {
	const res = [];

	datas &&
		datas.forEach(data => {
			const timeRegDate = Util.CreateNonUtcMomentDate(data.year, data.month, data.day);
			const dayIdx = timeRegDate.weekday();
			const taskId = data.task ? data.task.id : 'project_time';
			const taskIdx = res.findIndex(r => {
				return r.task.id === taskId;
			});

			if (taskIdx > -1) {
				res[taskIdx].task.totals[dayIdx] += data.minutesRegistered;
				res[taskIdx].task.billableTotals[dayIdx] += data.billableMinutesRegistered;
				res[taskIdx].task.timeRegsOnDayIndex[dayIdx].push(data);
				res[taskIdx].task.total += data.minutesRegistered;
				res[taskIdx].task.billableTotal += data.billableMinutesRegistered;
				data.notes && res[taskIdx].task.timeRegistrationsWithNotes.push(data);
			} else {
				const timeRegsTotals = [null, null, null, null, null, null, null];
				const timeRegsOnDayIndex = [[], [], [], [], [], [], []];
				const billableTimeRegsTotals = [null, null, null, null, null, null, null];
				timeRegsTotals[dayIdx] = data.minutesRegistered;
				timeRegsOnDayIndex[dayIdx].push(data);
				billableTimeRegsTotals[dayIdx] = data.billableMinutesRegistered;

				if (data.task) {
					//only used for the estimation so on task level
					const storyPointConversion =
						data.task && data.task.project.estimationUnit === ESTIMATION_UNIT.POINTS
							? data.task.project.minutesPerEstimationPoint
							: 1;
					res.push({
						task: {
							id: taskId,
							taskId: data.task.companyTaskId,
							name: data.task.name,
							timeRegistrationsWithNotes: data.notes ? [data] : [],
							totals: timeRegsTotals,
							billableTotals: billableTimeRegsTotals,
							timeRegsOnDayIndex: timeRegsOnDayIndex,
							total: data.minutesRegistered,
							billableTotal: data.billableMinutesRegistered,
							estimated: data.task.estimateForecast * storyPointConversion,
							totalTaskTimeRegs: data.task.totalMinutesRegistered,
						},
					});
				} else {
					//TimeRegs on project. Keep the object as Task, to avoid more if
					res.push({
						task: {
							id: taskId,
							timeRegistrationsWithNotes: data.notes ? [data] : [],
							timeRegsOnDayIndex: timeRegsOnDayIndex,
							totals: timeRegsTotals,
							billableTotals: billableTimeRegsTotals,
							total: data.minutesRegistered,
							billableTotal: data.billableMinutesRegistered,
						},
					});
				}
			}
		});
	return res;
};

export const getEditTimeRegsPropsFromRowData = (data, index) => {
	const timeRegsOnDay = data.timeRegsOnDayIndex ? data.timeRegsOnDayIndex[index] : undefined;
	if (!timeRegsOnDay?.length) {
		return {
			dayRegNodes: undefined,
			dayOfInput: undefined,
		};
	}
	const dayRegNodes = timeRegsOnDay.map(t => {
		return {node: t};
	});

	const firstTimeRegOnDay = timeRegsOnDay?.[0];

	const momentDate = Util.CreateNonUtcMomentDate(firstTimeRegOnDay.year, firstTimeRegOnDay.month, firstTimeRegOnDay.day);

	return {
		dayRegNodes: dayRegNodes,
		dayOfInput: momentDate,
	};
};

export const getNoApprovalStatusChangeMessage = (noApprovalStatusChange, formatMessage) => {
	const noApprovalChangeGenericMessage = formatMessage({id: 'approval.no_approval_status_change'});
	switch (noApprovalStatusChange) {
		case NO_APPROVAL_STATUS_CHANGE_REASON.INVOICED:
			return 'You cannot reject this timesheet because all time registrations are invoiced';
		case NO_APPROVAL_STATUS_CHANGE_REASON.LOCKED_PERIOD:
			return noApprovalChangeGenericMessage + formatMessage({id: 'approval.no_approval_status_change_locked_period'});
		case NO_APPROVAL_STATUS_CHANGE_REASON.COMPANY_LOCK:
			return noApprovalChangeGenericMessage + formatMessage({id: 'approval.no_approval_status_change_company_lock'});
		case NO_APPROVAL_STATUS_CHANGE_REASON.HAS_LINKED_ALLOCATION:
			return (
				noApprovalChangeGenericMessage + formatMessage({id: 'approval.no_approval_status_change_has_linked_allocation'})
			);
		default:
			return null;
	}
};

export const getButtonReminder = (sectionType, intl) => {
	switch (sectionType) {
		case APPROVAL_STATUS.NOT_SUBMITTED:
		case APPROVAL_STATUS.PENDING:
			return {action: APPROVAL_TYPE.SUBMIT, text: intl.formatMessage({id: 'time_approval.submit'})};
		case APPROVAL_STATUS.DECLINED:
			return {action: APPROVAL_TYPE.RESUBMIT, text: intl.formatMessage({id: 'time_approval.submit'})};
		default:
			return {action: null, text: ''};
	}
};

export const groupTimeRegsByEffectiveStatus = timeRegs => {
	const groups = {};
	timeRegs.forEach(tReg => {
		const bucket =
			hasNewApprovalStatuses() && (tReg.invoiced || tReg.approvalStatus === APPROVAL_STATUS.INVOICED)
				? APPROVAL_STATUS.INVOICED
				: tReg.approvalStatus;
		if (!groups[bucket]) groups[bucket] = [];
		groups[bucket].push(tReg);
	});
	return groups;
};

export const ownedProjectPersonsQuery = graphql`
	query TimeApprovalLogicQuery($personId: ID, $weekStart: String, $weekEnd: String) {
		viewer {
			id
			actualPersonId
			company {
				id
				person(id: $personId) {
					id
					ownedProjectPersons(first: 1000000) {
						edges {
							node {
								id
								firstName
								lastName
								fullName
								profilePictureId
								submitLockedDateYear
								submitLockedDateMonth
								submitLockedDateDay
								excludeFromCompanyLockedPeriod
								monday
								tuesday
								wednesday
								thursday
								friday
								saturday
								sunday
								startDate
								endDate
								createdAt
								holidayCalendar {
									id
									holidayCalendarEntries(first: 10000) {
										edges {
											node {
												id
												day
												month
												year
											}
										}
									}
								}
								department {
									id
								}
								role {
									id
								}
								permissions
								# PIKA-519: week-level approval-state rows for 0h-week
								# APPROVE/DECLINE bucket synthesis. Scoped to the viewing week so the
								# payload stays small. Synthesis step 2 reads these in
								# getApprovalStatusForWeek.
								weekApprovals(startDate: $weekStart, endDate: $weekEnd) {
									weekStartYear
									weekStartMonth
									weekStartDay
									approvalStatus
									declineReason
								}
							}
						}
					}
				}
			}
		}
	}
`;

/* export const ownedProjectPersonsQuery = graphql`
	fragment TimeApprovalLogic_viewer on Viewer @argumentDefinitions(personId: {type: "ID"}) {
		id
		actualPersonId
		company {
			id
			person(id: $personId) {
				id
				ownedProjectPersons(first: 1000) {
					edges {
						node {
							id
							firstName
							lastName
							profilePictureId
						}
					}
				}
			}
		}
	}
`;
 */
